import { useState } from "react";
import { Link } from "react-router-dom";
import type { Difficulty } from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { GameIcon } from "../../components/illustrations/GameIcon.js";
import { DifficultyInfoBox } from "../../components/game/DifficultyInfoBox.js";
import { useI18n } from "../../i18n/index.js";
import { readLastOptions, writeLastOptions } from "../../lib/lastOptions.js";
import { ROUND_TIME_SECONDS } from "../../lib/roundTime.js";
import styles from "./HuessworkStart.module.css";

export interface HuessworkOptions {
  difficulty: Difficulty;
}

const DIFFICULTIES: Difficulty[] = ["easy", "normal", "hard"];

export function HuessworkStart({ onStart }: { onStart: (options: HuessworkOptions) => void }) {
  const { t } = useI18n();
  const saved = readLastOptions<HuessworkOptions>("huesswork");
  const [difficulty, setDifficulty] = useState<Difficulty>(saved?.difficulty ?? "normal");

  function handleStart() {
    const options: HuessworkOptions = { difficulty };
    writeLastOptions("huesswork", options);
    onStart(options);
  }

  return (
    <main className={styles.main}>
      <GameIcon gameId="huesswork" className={styles.icon} />
      <h1 className={styles.title}>{GAME_BRANDING.huesswork.name}</h1>
      <p className={styles.tagline}>{t("huesswork.tagline")}</p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t("timeslip.start.difficultyLabel")}</h2>
        <div className={styles.segmented} role="group" aria-label={t("timeslip.start.difficultyLabel")}>
          {DIFFICULTIES.map((d) => (
            <button
              key={d}
              type="button"
              className={styles.segment}
              aria-pressed={difficulty === d}
              onClick={() => setDifficulty(d)}
            >
              {t(`timeslip.start.difficulty.${d}`)}
            </button>
          ))}
        </div>
        <DifficultyInfoBox
          description={t(`huesswork.start.difficultyInfo.${difficulty}`)}
          roundLimitSeconds={ROUND_TIME_SECONDS[difficulty]}
          noLimitLabel={t("common.noTimeLimit")}
        />
      </section>

      <button type="button" className={styles.startButton} onClick={handleStart}>
        {t("timeslip.start.startButton")}
      </button>
      <Link to="/" className={styles.backLink}>
        ← {t("common.backHome")}
      </Link>
    </main>
  );
}
